import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { money } from "@/lib/format";

const STATUS_LABELS: Record<string, string> = {
  nuevo: "Pago pendiente",
  pagado: "Pagado",
  intake_pendiente: "Formulario pendiente",
  en_proceso: "En proceso",
  esperando_cliente: "Esperando cliente",
  completado: "Completado",
  cancelado: "Cancelado",
};

export const Route = createFileRoute("/admin/clientes/$id")({ component: ClienteDetalle });

function ClienteDetalle() {
  const { id } = Route.useParams();
  const [c, setC] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [intakes, setIntakes] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const [{ data: cust }, { data: o }] = await Promise.all([
        supabase.from("customers").select("*").eq("id", id).maybeSingle(),
        supabase.from("orders").select("id,status,total,created_at,paid_at,intake_completed").eq("customer_id", id).order("created_at", { ascending: false }),
      ]);
      setC(cust); setOrders(o ?? []);
      const orderIds = (o ?? []).map((x: any) => x.id);
      if (orderIds.length > 0) {
        const { data: f } = await supabase.from("intake_forms").select("id,order_id,submitted_at,legal_full_name,email").in("order_id", orderIds).order("submitted_at", { ascending: false });
        setIntakes(f ?? []);
      }
    })();
  }, [id]);

  if (!c) return <div className="text-muted-foreground">Cargando…</div>;

  const paidOrders = orders.filter((o) => o.status !== "nuevo" && o.status !== "cancelado");
  const pendingOrders = orders.filter((o) => o.status === "nuevo");
  const totalPaid = paidOrders.reduce((a, o) => a + Number(o.total ?? 0), 0);
  const lastOrder = orders[0]; // already ordered desc

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/clientes" className="text-xs text-muted-foreground hover:text-foreground">← Volver a clientes</Link>
        <h2 className="mt-1 font-display text-xl font-semibold text-foreground">{c.full_name}</h2>
        <p className="text-sm text-muted-foreground">{c.email} · {c.phone ?? "sin teléfono"}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Stat label="Pedidos totales" value={String(orders.length)} />
        <Stat label="Pedidos pagados" value={String(paidOrders.length)} />
        <Stat label="Pedidos pendientes" value={String(pendingOrders.length)} />
        <Stat label="Total pagado" value={money(totalPaid)} accent />
        <Stat label="Formularios enviados" value={String(intakes.length)} />
        <Stat label="Último pedido" value={lastOrder ? new Date(lastOrder.created_at).toLocaleDateString() : "—"} />
      </div>

      <div className="rounded-lg border border-hairline bg-surface p-5">
        <h3 className="font-display text-sm uppercase tracking-wider text-label-accent">Información</h3>
        <dl className="mt-3 grid gap-3 md:grid-cols-2">
          <Field label="Compañía">{c.company_name ?? "—"}</Field>
          <Field label="Estado">{c.state ?? "—"}</Field>
          <Field label="Contacto preferido">{c.preferred_contact ?? "—"}</Field>
          <Field label="Cuenta cliente">{c.user_id ? "Activa 👤" : "No registrado"}</Field>
          <Field label="Cliente desde">{new Date(c.created_at).toLocaleDateString()}</Field>
        </dl>
      </div>

      <div className="rounded-lg border border-hairline bg-surface p-5">
        <h3 className="font-display text-sm uppercase tracking-wider text-label-accent">Pedidos</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="py-2">Fecha</th>
                <th className="py-2">ID</th>
                <th className="py-2">Estado</th>
                <th className="py-2">Total</th>
                <th className="py-2">Pago</th>
                <th className="py-2">Form</th>
                <th className="py-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-hairline">
                  <td className="py-2 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="py-2 font-mono text-[11px] text-muted-foreground">{o.id.slice(0, 8)}</td>
                  <td className="py-2 text-xs">{STATUS_LABELS[o.status] ?? o.status}</td>
                  <td className="py-2">{money(o.total)}</td>
                  <td className="py-2 text-xs">{o.paid_at ? "✓" : "—"}</td>
                  <td className="py-2 text-xs">{o.intake_completed ? "✓" : "—"}</td>
                  <td className="py-2 text-right">
                    <Link
                      to="/admin/pedidos/$id"
                      params={{ id: o.id }}
                      className="inline-flex items-center rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/20"
                    >
                      Ver pedido →
                    </Link>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-sm text-muted-foreground">Sin pedidos.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-hairline bg-surface p-5">
        <h3 className="font-display text-sm uppercase tracking-wider text-label-accent">Formularios enviados</h3>
        <ul className="mt-3 space-y-2">
          {intakes.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-hairline bg-surface-2 p-2 text-sm">
              <span className="min-w-0 flex-1 text-foreground">
                {f.legal_full_name ?? "—"}
                <span className="text-muted-foreground"> · {f.email ?? "—"} · {new Date(f.submitted_at).toLocaleString()}</span>
              </span>
              <span className="flex flex-wrap gap-2">
                <Link
                  to="/admin/formularios/$id"
                  params={{ id: f.id }}
                  className="inline-flex items-center rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/20"
                >
                  Ver formulario →
                </Link>
                <Link
                  to="/admin/pedidos/$id"
                  params={{ id: f.order_id }}
                  className="inline-flex items-center rounded-md border border-hairline bg-surface px-2.5 py-1 text-xs text-foreground hover:bg-surface-2"
                >
                  Ver pedido →
                </Link>
              </span>
            </li>
          ))}
          {intakes.length === 0 && <li className="text-xs text-muted-foreground">Sin formularios enviados.</li>}
        </ul>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`rounded-md border p-4 ${accent ? "border-primary/40 bg-primary/5" : "border-hairline bg-surface"}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-semibold text-foreground">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{children}</dd>
    </div>
  );
}
